import React from 'react';
import { CheckCircle2, ArrowRight, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { caseStudies } from '../data/mock';

const scrollToContact = () => {
  const el = document.getElementById('contact');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const accentClasses = {
  orange: {
    border: 'border-atob-orange/40 hover:border-atob-orange',
    badge: 'bg-atob-orange text-atob-blue',
    tag: 'bg-atob-orange/10 border-atob-orange/30 text-atob-orange'
  },
  blue: {
    border: 'border-blue-400/30 hover:border-blue-400/60',
    badge: 'bg-blue-400 text-atob-blue',
    tag: 'bg-blue-400/10 border-blue-400/30 text-blue-300'
  }
};

export const CaseStudies = () => {
  return (
    <section
      id="referenzen"
      data-testid="case-studies-section"
      className="relative py-24 md:py-28 bg-white overflow-hidden"
    >
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
          <span className="inline-flex items-center gap-2 text-atob-orange font-semibold text-sm uppercase tracking-[0.2em] mb-5">
            <span className="h-px w-8 bg-atob-orange"></span>
            Referenz-Projekte
            <span className="h-px w-8 bg-atob-orange"></span>
          </span>
          <h2
            data-testid="case-studies-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-atob-blue leading-[1.05] mb-6"
          >
            Was aus unserem{' '}
            <span className="text-atob-orange italic font-serif">Netzwerk entsteht</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Drei Beispiele, wie wir zusammen mit unseren Partnern gelöst haben, was Eigentümer,
            Anleger und Erbengemeinschaften wirklich beschäftigt.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 md:gap-8 max-w-7xl mx-auto mb-16">
          {caseStudies.map((cs, idx) => {
            const styles = accentClasses[cs.accent] || accentClasses.orange;
            return (
              <article
                key={cs.id}
                data-testid={`case-study-${cs.id}`}
                className={`group relative bg-white border-2 rounded-2xl p-7 md:p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl flex flex-col ${styles.border}`}
                style={{ animation: `fadeInUp 0.5s ease-out ${idx * 0.12}s both` }}
              >
                {/* Category ribbon */}
                <div className={`inline-flex self-start items-center gap-1.5 ${styles.badge} text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-5 shadow-sm`}>
                  <Tag className="h-3 w-3" />
                  {cs.category}
                </div>

                <h3 className="text-xl md:text-2xl font-bold text-atob-blue mb-4 leading-tight group-hover:text-atob-orange transition-colors duration-300">
                  {cs.title}
                </h3>

                <div className="space-y-4 mb-6 flex-1">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-1">Situation</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{cs.problem}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-1">Lösung</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{cs.solution}</p>
                  </div>
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-[11px] font-black uppercase tracking-widest text-atob-orange mb-1">Ergebnis</p>
                    <p className="text-sm text-atob-blue font-semibold leading-relaxed flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-atob-orange flex-shrink-0 mt-0.5" />
                      <span>{cs.result}</span>
                    </p>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-auto">
                  {cs.tags.map((t) => (
                    <span
                      key={t}
                      className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${styles.tag}`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gray-600 mb-5">
            Ihr Fall ist anders? Genau deshalb sprechen wir vor jedem Auftrag persönlich.
          </p>
          <Button
            onClick={scrollToContact}
            data-testid="case-studies-cta"
            size="lg"
            className="bg-atob-blue hover:bg-atob-orange text-white font-bold px-8 py-6 text-base transition-colors duration-300"
          >
            Kostenlose Erstberatung anfragen
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};
